/* ============================================================================
   Skèmino — theme.js
   Core interactions: mobile nav, AJAX cart drawer, accordion, scroll reveal,
   toast. Vanilla ES modules, no dependencies, no build step. Loaded `defer`.
   ========================================================================== */
(function () {
  "use strict";

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const money = (cents) =>
    (window.Skemino && window.Skemino.formatMoney
      ? window.Skemino.formatMoney(cents)
      : (cents / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" }));

  /* --------------------------------------------------------------------- */
  /* Toast                                                                  */
  /* --------------------------------------------------------------------- */
  let toastEl, toastTimer;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(() => toastEl.classList.add("is-visible"));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("is-visible"), 3200);
  }
  window.Skemino = window.Skemino || {};
  window.Skemino.toast = toast;

  /* --------------------------------------------------------------------- */
  /* Mobile navigation                                                      */
  /* --------------------------------------------------------------------- */
  const navToggle = $("[data-nav-toggle]");
  const nav = $("[data-site-nav]");
  if (navToggle && nav) {
    navToggle.addEventListener("click", () => {
      const open = nav.classList.toggle("is-open");
      navToggle.setAttribute("aria-expanded", String(open));
      document.body.classList.toggle("no-scroll", open);
    });
    $$("a", nav).forEach((a) =>
      a.addEventListener("click", () => {
        nav.classList.remove("is-open");
        navToggle.setAttribute("aria-expanded", "false");
        document.body.classList.remove("no-scroll");
      })
    );
  }

  /* --------------------------------------------------------------------- */
  /* Accordion (FAQ)                                                        */
  /* --------------------------------------------------------------------- */
  $$("[data-accordion] .accordion__trigger").forEach((trigger) => {
    trigger.addEventListener("click", () => {
      const expanded = trigger.getAttribute("aria-expanded") === "true";
      const single = trigger.closest("[data-accordion]").hasAttribute("data-accordion-single");
      if (single && !expanded) {
        $$(".accordion__trigger", trigger.closest("[data-accordion]")).forEach((t) =>
          t.setAttribute("aria-expanded", "false")
        );
      }
      trigger.setAttribute("aria-expanded", String(!expanded));
    });
  });

  /* --------------------------------------------------------------------- */
  /* Scroll reveal                                                          */
  /* --------------------------------------------------------------------- */
  const revealEls = $$("[data-reveal], [data-reveal-stagger]");
  if (revealEls.length) {
    if (!("IntersectionObserver" in window)) {
      revealEls.forEach((el) => el.classList.add("is-visible"));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
      );
      revealEls.forEach((el) => io.observe(el));
    }
  }

  /* --------------------------------------------------------------------- */
  /* Cart drawer (AJAX)                                                     */
  /* --------------------------------------------------------------------- */
  const drawer = $("[data-cart-drawer]");
  const overlay = $("[data-overlay]");
  const cartBody = $("[data-cart-body]");
  const cartFoot = $("[data-cart-foot]");
  const cartCounts = $$("[data-cart-count]");

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add("is-open");
    overlay && overlay.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("no-scroll");
    const closeBtn = $("[data-cart-close]", drawer);
    closeBtn && closeBtn.focus();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove("is-open");
    overlay && overlay.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("no-scroll");
  }
  window.Skemino.openCart = openDrawer;
  window.Skemino.closeCart = closeDrawer;

  $$("[data-cart-open]").forEach((b) =>
    b.addEventListener("click", (e) => {
      e.preventDefault();
      openDrawer();
      refreshCart({ notify: true });
    })
  );
  $$("[data-cart-close]").forEach((b) => b.addEventListener("click", closeDrawer));
  overlay && overlay.addEventListener("click", closeDrawer);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeDrawer();
      if (nav && nav.classList.contains("is-open") && navToggle) navToggle.click();
    }
  });

  const strings = () => window.Skemino.strings || {};
  /* Only allow same-origin relative URLs from cart API → blocks javascript: etc. */
  const safeUrl = (u) => (typeof u === "string" && /^\/(?!\/)/.test(u) ? u : "#");

  function updateCount(count) {
    cartCounts.forEach((el) => {
      const changed = el.textContent !== String(count);
      el.textContent = count;
      el.hidden = count === 0;
      if (changed && count > 0) {
        el.classList.remove("is-updating");
        void el.offsetWidth; /* restart animation */
        el.classList.add("is-updating");
      }
    });
  }

  /* Brand empty state: faint 6x6 board motif + invitation (no innerHTML). */
  function buildEmptyState() {
    const wrap = document.createElement("div");
    wrap.className = "cart-empty";
    const board = document.createElement("div");
    board.className = "cart-empty__board";
    board.setAttribute("aria-hidden", "true");
    for (let i = 0; i < 36; i++) board.appendChild(document.createElement("span"));
    const h = document.createElement("h3");
    h.textContent = strings().cartEmptyTitle || "La tua scacchiera aspetta.";
    const p = document.createElement("p");
    p.textContent = strings().cartEmpty || "Il tuo carrello è vuoto.";
    wrap.append(board, h, p);
    const allProducts = window.Skemino.routes && window.Skemino.routes.allProducts;
    if (allProducts) {
      const a = document.createElement("a");
      a.className = "link-arrow";
      a.href = safeUrl(allProducts);
      a.textContent = strings().cartExplore || "Esplora la collezione";
      wrap.appendChild(a);
    }
    return wrap;
  }

  /* Build a line item via DOM API — every value escaped, no HTML injection. */
  function buildLineItem(item) {
    const row = document.createElement("div");
    row.className = "line-item";
    row.setAttribute("data-line-item", "");
    row.dataset.key = item.key;

    const media = document.createElement("a");
    media.className = "line-item__media";
    media.href = safeUrl(item.url);
    if (item.image) {
      const img = document.createElement("img");
      img.src = item.image.replace(/(\.[^.]+)$/, "_160x$1");
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.width = 72;
      img.height = 72;
      media.appendChild(img);
    }

    const info = document.createElement("div");
    const title = document.createElement("a");
    title.className = "line-item__title";
    title.href = safeUrl(item.url);
    title.textContent = item.product_title;
    info.appendChild(title);
    if (!item.product_has_only_default_variant) {
      const meta = document.createElement("div");
      meta.className = "line-item__meta";
      meta.textContent = item.variant_title || "";
      info.appendChild(meta);
    }

    const qtyWrap = document.createElement("div");
    qtyWrap.className = "qty";
    qtyWrap.setAttribute("data-qty", "");
    const down = document.createElement("button");
    down.type = "button";
    down.setAttribute("data-qty-down", "");
    down.setAttribute("aria-label", strings().decrease || "Diminuisci");
    down.textContent = "−";
    const qInput = document.createElement("input");
    qInput.type = "text";
    qInput.inputMode = "numeric";
    qInput.value = item.quantity;
    qInput.setAttribute("data-qty-input", "");
    qInput.readOnly = true;
    const up = document.createElement("button");
    up.type = "button";
    up.setAttribute("data-qty-up", "");
    up.setAttribute("aria-label", strings().increase || "Aumenta");
    up.textContent = "+";
    qtyWrap.append(down, qInput, up);
    info.appendChild(qtyWrap);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "line-item__remove";
    remove.setAttribute("data-remove", "");
    remove.textContent = strings().remove || "Rimuovi";
    info.appendChild(remove);

    const price = document.createElement("div");
    price.className = "line-item__price";
    price.textContent = money(item.final_line_price);

    row.append(media, info, price);
    return row;
  }

  function renderCart(cart) {
    updateCount(cart.item_count);
    if (!cartBody) return;
    cartBody.textContent = "";
    if (cart.item_count === 0) {
      cartBody.appendChild(buildEmptyState());
      if (cartFoot) cartFoot.hidden = true;
      return;
    }
    if (cartFoot) cartFoot.hidden = false;
    const frag = document.createDocumentFragment();
    cart.items.forEach((item) => frag.appendChild(buildLineItem(item)));
    cartBody.appendChild(frag);

    if (cartFoot) {
      const subtotal = $("[data-cart-subtotal]", cartFoot);
      if (subtotal) subtotal.textContent = money(cart.total_price);
    }
    bindLineItems();
  }

  function bindLineItems() {
    $$("[data-line-item]", cartBody).forEach((row) => {
      const key = row.dataset.key;
      const input = $("[data-qty-input]", row);
      const qty = () => {
        const v = parseInt(input.value, 10);
        return isNaN(v) ? 1 : v;
      };
      $("[data-qty-up]", row).addEventListener("click", () => changeLine(key, qty() + 1));
      $("[data-qty-down]", row).addEventListener("click", () => changeLine(key, Math.max(0, qty() - 1)));
      $("[data-remove]", row).addEventListener("click", () => changeLine(key, 0));
    });
  }

  async function changeLine(id, quantity) {
    try {
      const res = await fetch(window.Skemino.routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id, quantity }),
      });
      if (!res.ok) throw new Error("Cart change failed: " + res.status);
      renderCart(await res.json());
    } catch (err) {
      toast(strings().cartError || "Impossibile aggiornare il carrello.");
    }
  }

  async function refreshCart(opts) {
    try {
      const res = await fetch(window.Skemino.routes.cart + ".js", { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("Cart fetch failed: " + res.status);
      renderCart(await res.json());
    } catch (err) {
      /* Surface the error only when the user explicitly opened the cart. */
      if (opts && opts.notify) toast(strings().cartError || "Impossibile caricare il carrello.");
    }
  }

  /* Add-to-cart (event delegation across forms with data-product-form) */
  let isSubmitting = false;
  async function addToCart(form) {
    if (isSubmitting) return; /* guard against rapid double-submit (Enter + click) */
    isSubmitting = true;
    const submitBtn = form.querySelector('[type="submit"]');
    const original = submitBtn ? submitBtn.innerHTML : "";
    if (submitBtn) {
      submitBtn.setAttribute("aria-disabled", "true");
      submitBtn.innerHTML = window.Skemino.strings ? window.Skemino.strings.adding : "Aggiungo…";
    }
    try {
      const formData = new FormData(form);
      const res = await fetch(window.Skemino.routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        toast(err.description || "Articolo non disponibile.");
        return;
      }
      await refreshCart();
      openDrawer();
      toast(window.Skemino.strings ? window.Skemino.strings.added : "Aggiunto al carrello.");
    } catch (err) {
      toast("Qualcosa è andato storto. Riprova.");
    } finally {
      isSubmitting = false;
      if (submitBtn) {
        submitBtn.removeAttribute("aria-disabled");
        submitBtn.innerHTML = original;
      }
    }
  }

  document.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-product-form]");
    if (!form) return;
    e.preventDefault();
    addToCart(form);
  });

  /* Prime the count on load */
  if (cartCounts.length) refreshCart();
})();
